import { useEffect, useMemo, useState } from "react";
import PlaylistData from "./data/PlaylistData";
import { Route, Routes } from "react-router-dom";
import { PlaylistRoutePaths } from "./PlaylistRoutePaths";
import { PlaylistItemData } from "./data/PlaylistItemData";
import { SongsheetBeamerView } from "../beamer/SongsheetBeamerView";
import Auth from "../auth/Auth";
import { SongsheetCarousel } from "../carousel/SongsheetCarousel";
import { Client } from "urql";
import {
  DeviceInfoContext,
  useDeviceContextStateProvider,
} from "../device/DeviceInfoContext";
import { HomeLocalStorageKeys } from "../HomeLocalStorageKeys";
import {
  CrewLiveContext,
  useCrewLiveContextStateProvider,
} from "../live/CrewLiveContext";
import LocalStoragePersistence from "../localStoragePersistence/common/LocalStoragePersistence";
import { InvalidRouteElement } from "../InvalidRouteElement";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { useCrewsQuery } from "../crew/useCrewsQuery";
import { CrewInformationUb } from "../gql/graphql";
import { usePlaylist } from "./graphql/usePlaylists";
import { toPreviewPlaylist, useRecentPreviews } from "./preview/RecentPreviews";
import { formatDateTimeString } from "../helper/formatDateTime";
import { LoadingWithHeader } from "../common/LoadingWithHeader";
import { LinkOpeningProblemAlert } from "./LinkOpeningProblemAlert";
import { useTranslation } from "react-i18next";

interface Props {
  playlistIdentifier: string;
  lookupSong: LookupSongFunction;
  auth: Auth;
  graphQlClient: Client;
}

/** The playlist from the cache, or the preview from a link or QR code until the real one is available. */
function Playlist(props: Props) {
  const { t } = useTranslation();
  const cachedPlaylist = usePlaylist(props.playlistIdentifier);
  const recentPreview = useRecentPreviews().find(
    (p) => p.id === props.playlistIdentifier,
  );
  const preview = useMemo(
    () =>
      recentPreview &&
      toPreviewPlaylist(
        recentPreview,
        t("playlist.preview.defaultTitle", {
          date: formatDateTimeString(recentPreview.openedAt),
        }),
      ),
    [recentPreview, t],
  );
  const playlistData = cachedPlaylist ?? preview;

  if (playlistData) {
    return <PlaylistView {...props} playlistData={playlistData} />;
  }
  if (cachedPlaylist === undefined) {
    return <LoadingWithHeader message={t("app.loadingMessage")} />;
  }
  return (
    <LinkOpeningProblemAlert>
      Die gesuchte Playlist konnte nicht gefunden werden.
    </LinkOpeningProblemAlert>
  );
}

function PlaylistView(props: Props & { playlistData: PlaylistData }) {
  const { playlistData } = props;
  const currentCrewIdentifier = playlistData.belongsToCrew;

  const { crews } = useCrewsQuery();
  const [currentCrewMaybe, setCurrentCrewMaybe] = useState<
    CrewInformationUb | undefined
  >(undefined);

  useEffect(() => {
    const currentCrew = crews?.find(
      (c) => c.identifier === currentCrewIdentifier,
    );
    setCurrentCrewMaybe(currentCrew);
  }, [crews, currentCrewIdentifier]);

  //persist the crew identifier of this playlist as the current crew
  useEffect(() => {
    LocalStoragePersistence.persist(
      HomeLocalStorageKeys.currentCrewIdentifierKey,
      currentCrewIdentifier,
    );
  }, [currentCrewIdentifier]);

  const deviceContextState = useDeviceContextStateProvider();

  const crewLiveContextState = useCrewLiveContextStateProvider(
    currentCrewIdentifier,
  );

  const [activeItemInCarousel, setActiveItemInCarousel] = useState<
    PlaylistItemData | undefined
  >(undefined);

  return (
    <DeviceInfoContext.Provider value={deviceContextState}>
      <CrewLiveContext.Provider value={crewLiveContextState}>
        <Routes>
          <Route
            path={`/${PlaylistRoutePaths.CAROUSEL}`}
            element={
              <SongsheetCarousel
                selectedItem={activeItemInCarousel}
                setSelectedItem={setActiveItemInCarousel}
                {...props}
                bucket={currentCrewMaybe?.bucket}
                graphQlClient={props.graphQlClient}
              />
            }
          />
          <Route
            path={`/${PlaylistRoutePaths.BEAMER_VIEW}`}
            element={<SongsheetBeamerView />}
          />
          <Route path="*" element={<InvalidRouteElement />} />
        </Routes>
      </CrewLiveContext.Provider>
    </DeviceInfoContext.Provider>
  );
}

export default Playlist;
